import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Circle, ClipboardList, Eye, PencilLine, X } from 'lucide-react';
import AppShell from '../app/AppShell';
import {
  ActionIconButton,
  CustomDatePicker,
  FeedbackMessage,
  FilterField,
  FilterPanel,
  ModalBackdrop,
  PaginationFooter,
  SaveConfirmationModal,
  ToastStack,
} from '../components/common';
import { PurchaseOrderStatusBadge } from '../components/purchase-order';
import { ReceiptCaptureButton } from '../components/receiving';
import useReceiptAutofill from '../hooks/useReceiptAutofill';
import useReceiving from '../hooks/useReceiving';
import { buildAssetUrl } from '../services/apiConfig';
import { formatShortDate } from '../utils/formatters';

const receiptDetailFieldNames = ['netKg'];
const OCR_IN_PROGRESS_LEAVE_MESSAGE =
  'Receipt OCR is still processing. Leaving now may lose the autofill result.';

function StaffReceivingPage({ navigationItems, activePath, onNavigate }) {
  const [isConfirmingSubmit, setIsConfirmingSubmit] = useState(false);
  const [isReceivingModalOpen, setIsReceivingModalOpen] = useState(false);
  const [pendingReceivingItems, setPendingReceivingItems] = useState([]);
  const {
    filteredReceivingOrders,
    filterValues,
    receivingPagination,
    receivingSearchSuggestions,
    selectedPurchaseOrder,
    itemValues,
    receiptFieldErrors,
    isLoading,
    isSaving,
    loadError,
    submitError,
    successMessage,
    selectPurchaseOrder,
    handleItemChange,
    handleDefaultZeroBlur,
    handleInboundWeightBlur,
    handleWeightComputationBlur,
    handleFilterChange,
    handleFilterSubmit,
    handleFilterReset,
    applyReceiptAutofill,
    clearReceiptAutofillValues,
    addItem,
    cancelItem,
    submitReceiving,
  } = useReceiving();
  const toasts = [
    loadError ? { id: 'receiving-load-error', tone: 'error', message: loadError } : null,
    submitError ? { id: 'receiving-submit-error', tone: 'error', message: submitError } : null,
    successMessage
      ? { id: 'receiving-success', tone: 'success', message: successMessage }
      : null,
  ].filter(Boolean);

  async function handleConfirmSubmit() {
    setIsReceivingModalOpen(false);

    const isSubmitted = await submitReceiving(pendingReceivingItems);

    if (isSubmitted) {
      setIsConfirmingSubmit(false);
      setPendingReceivingItems([]);
    } else {
      setIsConfirmingSubmit(false);
      setIsReceivingModalOpen(true);
    }
  }

  function handleSubmitItem(receiptImageData = '') {
    const nextItems = addItem(receiptImageData);

    if (nextItems) {
      setPendingReceivingItems(Array.isArray(nextItems) ? nextItems : [nextItems]);
      setIsConfirmingSubmit(true);
    }
  }

  function handleCancelConfirmation() {
    setPendingReceivingItems([]);
    setIsConfirmingSubmit(false);
  }

  function handleOpenReceivingModal(purchaseOrder) {
    selectPurchaseOrder(purchaseOrder.id);
    setIsReceivingModalOpen(true);
  }

  function handleCloseReceivingModal() {
    cancelItem();
    setIsReceivingModalOpen(false);
  }

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div>
            <h2 className="m-0 text-[1.25rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)] sm:text-[1.4rem]">
              Receiving
            </h2>
            <p className="m-0 mt-2 text-[0.96rem] text-[var(--theme-text-muted)]">
              Receive in-progress supplier deliveries and record the actual seaweed stock.
            </p>
          </div>

          {isLoading ? (
            <ReceivingPageSkeleton />
          ) : (
            <>
              <ReceivingFilters
                values={filterValues}
                suggestions={receivingSearchSuggestions}
                onChange={handleFilterChange}
                onSubmit={handleFilterSubmit}
                onReset={handleFilterReset}
              />

              <div className="grid gap-3">
                <ReceivingItemsTable
                  purchaseOrders={filteredReceivingOrders}
                  onOpenReceivingModal={handleOpenReceivingModal}
                />
                {receivingPagination.totalItems ? (
                  <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
                    <PaginationFooter {...receivingPagination} />
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </section>

      {isReceivingModalOpen ? (
        <ReceivingEntryModal
          selectedPurchaseOrder={selectedPurchaseOrder}
          itemValues={itemValues}
          receiptFieldErrors={receiptFieldErrors}
          isSaving={isSaving}
          isConfirmingSubmit={isConfirmingSubmit}
          submitError={submitError}
          onReceiptAutofill={applyReceiptAutofill}
          onClearReceiptAutofill={clearReceiptAutofillValues}
          onItemChange={handleItemChange}
          onDefaultZeroBlur={handleDefaultZeroBlur}
          onInboundWeightBlur={handleInboundWeightBlur}
          onWeightComputationBlur={handleWeightComputationBlur}
          onSubmit={handleSubmitItem}
          onCancel={handleCloseReceivingModal}
        />
      ) : null}

      {isConfirmingSubmit ? (
        <ConfirmReceivingModal
          isDraft={selectedPurchaseOrder?.status === 'Initial'}
          isSaving={isSaving}
          onCancel={handleCancelConfirmation}
          onConfirm={handleConfirmSubmit}
        />
      ) : null}
    </AppShell>
  );
}

function ReceivingEntryModal({
  selectedPurchaseOrder,
  itemValues,
  receiptFieldErrors,
  isSaving,
  isConfirmingSubmit,
  submitError,
  onReceiptAutofill,
  onClearReceiptAutofill,
  onItemChange,
  onDefaultZeroBlur,
  onInboundWeightBlur,
  onWeightComputationBlur,
  onSubmit,
  onCancel,
}) {
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const [isOcrCancelConfirmOpen, setIsOcrCancelConfirmOpen] = useState(false);
  const initialReceiptImageUrl = getReceivingDraftReceiptImageUrl(selectedPurchaseOrder);
  const {
    isAutofilling,
    autofillError,
    autofillMessage,
    hasAutofilled,
    receiptPreviewUrl,
    handleReceiptSelect,
    handleReceiptAutofill,
    clearReceiptCapture,
  } = useReceiptAutofill({
    onAutofill: onReceiptAutofill,
    initialImageUrl: initialReceiptImageUrl,
  });
  const stepOneRef = useRef(null);
  const isReadOnlyMode = ['Received', 'Completed'].includes(selectedPurchaseOrder?.status);
  const isFormLocked = isSaving || isReadOnlyMode;
  const isReceiptDetailsLocked = isFormLocked || isAutofilling;
  const shouldShowValidationErrors =
    !isConfirmingSubmit && !isSaving && (hasTriedSubmit || Boolean(submitError));
  const receivingUnit = selectedPurchaseOrder?.stockUnit === 'kg' ? 'kg' : 'sacks';
  const requestedQuantity = Number(selectedPurchaseOrder?.expectedQuantity || 0);
  const netWeightOverageWarning =
    receivingUnit === 'kg' &&
    requestedQuantity > 0 &&
    Number(itemValues.netKg || 0) > requestedQuantity
      ? `Net KG is ${formatNumber(Number(itemValues.netKg || 0) - requestedQuantity)} kg over the requested ${formatNumber(requestedQuantity)} kg. Admin approval is required before balancing.`
      : '';
  const sacksOverageWarning =
    receivingUnit === 'sacks' &&
    requestedQuantity > 0 &&
    Number(itemValues.numberOfSacks || 0) > requestedQuantity
      ? `No. of Sacks is ${formatNumber(Number(itemValues.numberOfSacks || 0) - requestedQuantity)} sacks over the requested ${formatNumber(requestedQuantity)} sacks. Admin approval is required before balancing.`
      : '';
  const requiredWeightErrors = {
    netKg: getPositiveRequiredError(itemValues.netKg, 'Net KG'),
  };
  const receiptValidationErrors = {
    ...receiptFieldErrors,
    numberOfSacks: receiptFieldErrors.numberOfSacks,
    netKg: receiptFieldErrors.netKg || requiredWeightErrors.netKg,
  };
  const receiptDetailErrors = {
    ...receiptFieldErrors,
    numberOfSacks: receiptFieldErrors.numberOfSacks,
    netKg: receiptFieldErrors.netKg || (shouldShowValidationErrors ? requiredWeightErrors.netKg : ''),
  };
  const receiptImageError =
    !isConfirmingSubmit && !isSaving && hasTriedSubmit && !receiptPreviewUrl
      ? 'Upload receipt image before submitting.'
      : '';
  const dryQuantityValue = Number(itemValues.dryQuantityKg || 0);
  const wetQuantityValue = Number(itemValues.wetQuantityKg || 0);
  const rejectQuantityValue = Number(itemValues.rejectQuantityKg || 0);
  const isMoistureContentDisabled = isFormLocked || wetQuantityValue <= 0;
  const receivedOrderQuantity =
    receivingUnit === 'sacks'
      ? dryQuantityValue + wetQuantityValue
      : dryQuantityValue + wetQuantityValue + rejectQuantityValue;
  const receivedQuantityTotal = dryQuantityValue + wetQuantityValue + rejectQuantityValue;
  const quantityRequiredError =
    receivedQuantityTotal > 0 ? '' : `Enter a dry, wet, or reject quantity greater than 0.`;
  const quantityMismatchWarning =
    receivedOrderQuantity > 0 && requestedQuantity > 0 && receivedOrderQuantity < requestedQuantity
      ? `Received quantity total is ${formatNumber(receivedOrderQuantity)} ${receivingUnit}, below the requested ${formatNumber(requestedQuantity)} ${receivingUnit}.`
      : '';
  const quantityOverageWarning =
    receivedOrderQuantity > 0 && requestedQuantity > 0 && receivedOrderQuantity > requestedQuantity
      ? `Received quantity total is ${formatNumber(receivedOrderQuantity)} ${receivingUnit}, ${formatNumber(receivedOrderQuantity - requestedQuantity)} ${receivingUnit} over the requested ${formatNumber(requestedQuantity)} ${receivingUnit}. Admin approval is required before balancing.`
      : '';
  const isDraftMode = selectedPurchaseOrder?.status === 'Initial';
  const isReceiptDetailsComplete = receiptDetailFieldNames.every(
    (fieldName) =>
      String(itemValues[fieldName] || '').trim() &&
      !String(receiptValidationErrors[fieldName] || '').trim()
  );
  const hasValidReceivingQuantities =
    isValidStepTwoNumberValue(itemValues.dryQuantityKg) &&
    isValidStepTwoNumberValue(itemValues.wetQuantityKg) &&
    isValidStepTwoNumberValue(itemValues.rejectQuantityKg) &&
    receivedQuantityTotal > 0 &&
    (receivingUnit !== 'sacks' ||
      (Number.isInteger(Number(itemValues.dryQuantityKg)) &&
        Number.isInteger(Number(itemValues.wetQuantityKg))));
  const hasValidMoistureContent =
    isMoistureContentDisabled || isValidWholeNumberValue(itemValues.moistureContent);
  const moistureContentError = hasValidMoistureContent
    ? ''
    : 'Moisture content must be a valid whole number.';
  const canSubmitReceivingItem =
    !isReadOnlyMode &&
    isReceiptDetailsComplete &&
    hasValidReceivingQuantities &&
    hasValidMoistureContent;

  useEffect(() => {
    if (!isAutofilling) {
      return undefined;
    }

    function handleBeforeUnload(event) {
      event.preventDefault();
      event.returnValue = OCR_IN_PROGRESS_LEAVE_MESSAGE;
      return OCR_IN_PROGRESS_LEAVE_MESSAGE;
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isAutofilling]);

  useEffect(() => {
    if (!isAutofilling && isOcrCancelConfirmOpen) {
      setIsOcrCancelConfirmOpen(false);
    }
  }, [isAutofilling, isOcrCancelConfirmOpen]);

  function handleGuardedCancel() {
    if (isAutofilling) {
      setIsOcrCancelConfirmOpen(true);
      return;
    }

    onCancel();
  }

  function handleConfirmOcrCancel() {
    setIsOcrCancelConfirmOpen(false);
    onCancel();
  }

  function handleRetakeReceipt() {
    setHasTriedSubmit(false);
    clearReceiptCapture();
    onClearReceiptAutofill();

    window.requestAnimationFrame(() => {
      stepOneRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      stepOneRef.current?.focus({ preventScroll: true });
    });
  }

  function handleSubmitReceivingItem() {
    setHasTriedSubmit(true);

    if (!receiptPreviewUrl || !canSubmitReceivingItem) {
      return;
    }

    setHasTriedSubmit(false);
    onSubmit(receiptPreviewUrl);
  }

  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.14)]"
      contentClassName="!px-4"
      zIndexClassName="z-40"
    >
      <div className="mx-auto w-full max-w-6xl rounded-[26px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_22px_60px_rgba(var(--theme-shadow-rgb),0.14)] sm:p-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h3 className="m-0 text-[1.12rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
              {isReadOnlyMode ? 'View Receiving Entry' : isDraftMode ? 'Draft Receiving Entry' : 'Receiving Entry'}
            </h3>
            <p className="m-0 mt-1 text-[0.88rem] text-[var(--theme-text-muted)]">
              {isReadOnlyMode
                ? 'Review the receiving details saved for this purchase order.'
                : isDraftMode
                ? 'Save draft receiving details while this purchase order is still initial.'
                : 'Record the actual seaweed received for the selected purchase order.'}
            </p>
          </div>
          <button
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-[var(--theme-border-soft)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={handleGuardedCancel}
            disabled={isSaving}
            aria-label="Close receiving entry modal"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="grid gap-3">
          <div className="rounded-[16px] border border-[var(--theme-border-soft)] bg-white px-4 py-2.5">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="text-[0.68rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
                  Delivery Details
                </span>
                <h4 className="m-0 mt-0.5 text-[0.95rem] font-semibold tracking-[-0.02em] text-[var(--theme-text)]">
                  Selected Purchase Order
                </h4>
              </div>
              <span className="rounded-full bg-[var(--theme-accent-soft)] px-3 py-1 text-[0.72rem] font-semibold text-[var(--theme-text-subtle)]">
                {isReadOnlyMode ? 'Read Only' : isDraftMode ? 'Initial Draft' : selectedPurchaseOrder?.status || 'In Progress'}
              </span>
            </div>

            <div className="grid gap-x-5 gap-y-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
              <CompactInfoTile
                label="P.O. Code"
                value={selectedPurchaseOrder ? formatPurchaseOrderCode(selectedPurchaseOrder) : '-'}
              />
              <CompactInfoTile
                label="Supplier (Sacks Mark+Company)"
                value={
                  selectedPurchaseOrder
                    ? formatSupplierDisplay(
                      selectedPurchaseOrder.sacksMark,
                      selectedPurchaseOrder.supplierName
                    )
                    : '-'
                }
              />
              <CompactInfoTile
                label="Quantity"
                value={
                  selectedPurchaseOrder
                    ? formatQuantity(
                      selectedPurchaseOrder.expectedQuantity,
                      selectedPurchaseOrder.stockUnit
                    )
                    : '-'
                }
              />
              <CompactInfoTile
                label="Seaweed Variant"
                value={selectedPurchaseOrder?.seaweedType || '-'}
              />
              <CompactInfoTile label="Lansa Name" value={selectedPurchaseOrder?.lansaName || '-'} />
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[1.35fr_0.85fr]">
            <StepCard
              stepRef={stepOneRef}
              eyebrow="Step 1"
              stepNumber="1"
              title="Receipt Details"
              description="Review the receipt details and edit anything that was not captured correctly."
              status={hasAutofilled ? 'done' : 'current'}
              action={
                isReadOnlyMode ? null : (
                  <ReceiptCaptureButton
                    isLoading={isAutofilling}
                    onSelect={handleReceiptSelect}
                  />
                )
              }
            >
              {autofillError ? (
                <FeedbackMessage tone="error" message={autofillError} className="mb-3 text-[0.86rem]" />
              ) : null}
              {autofillMessage ? (
                <FeedbackMessage tone="success" message={autofillMessage} className="mb-3 text-[0.86rem]" />
              ) : null}
              {receiptPreviewUrl ? (
                <ReceiptPreview
                  imageUrl={receiptPreviewUrl}
                  isLoading={isAutofilling}
                  onReadReceipt={handleReceiptAutofill}
                  onRetake={handleRetakeReceipt}
                  isReadOnly={isReadOnlyMode}
                />
              ) : null}

              <div className="relative">
                <div
                  className={`grid gap-4 ${isAutofilling ? 'pointer-events-none select-none blur-[1px]' : ''
                    }`}
                >
                  <div className="grid gap-x-3 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-3">
                    <FieldGroup label="Scale Slip No." error={receiptDetailErrors.scaleSlipNo}>
                      <TextInput
                        name="scaleSlipNo"
                        value={itemValues.scaleSlipNo}
                        onChange={onItemChange}
                        placeholder="Enter scale slip no."
                        disabled={isReceiptDetailsLocked}
                        hasError={Boolean(receiptDetailErrors.scaleSlipNo)}
                      />
                    </FieldGroup>
                    <FieldGroup label="Mark" error={receiptDetailErrors.mark}>
                      <TextInput
                        name="mark"
                        value={itemValues.mark}
                        onChange={onItemChange}
                        placeholder="Enter sacks mark"
                        disabled={isReceiptDetailsLocked}
                        hasError={Boolean(receiptDetailErrors.mark)}
                      />
                    </FieldGroup>
                    <FieldGroup label="Description" error={receiptDetailErrors.description}>
                      <TextInput
                        name="description"
                        value={itemValues.description}
                        onChange={onItemChange}
                        placeholder="Enter seaweed description"
                        disabled={isReceiptDetailsLocked}
                        hasError={Boolean(receiptDetailErrors.description)}
                      />
                    </FieldGroup>
                    <FieldGroup label="No. of Sacks" error={receiptDetailErrors.numberOfSacks}>
                      <TextInput
                        name="numberOfSacks"
                        value={itemValues.numberOfSacks}
                        onChange={onItemChange}
                        type="text"
                        inputMode="numeric"
                        placeholder="Enter sacks count"
                        disabled={isReceiptDetailsLocked}
                        hasError={Boolean(receiptDetailErrors.numberOfSacks)}
                      />
                    </FieldGroup>
                    <FieldGroup label="Weigher" error={receiptDetailErrors.weigher}>
                      <TextInput
                        name="weigher"
                        value={itemValues.weigher}
                        onChange={onItemChange}
                        placeholder="Enter weigher"
                        disabled={isReceiptDetailsLocked}
                        hasError={Boolean(receiptDetailErrors.weigher)}
                      />
                    </FieldGroup>
                    <FieldGroup label="Representative" error={receiptDetailErrors.representative}>
                      <TextInput
                        name="representative"
                        value={itemValues.representative}
                        onChange={onItemChange}
                        placeholder="Enter representative"
                        disabled={isReceiptDetailsLocked}
                        hasError={Boolean(receiptDetailErrors.representative)}
                      />
                    </FieldGroup>
                  </div>

                  <div className="grid gap-3 rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3">
                    <h4 className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.1em] text-[var(--theme-text-muted)]">
                      Inbound Weighing
                    </h4>
                    <div className="grid gap-x-3 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-4">
                      <DateFieldGroup>
                        <CustomDatePicker
                          label="Date"
                          name="inboundDate"
                          value={itemValues.inboundDate}
                          onChange={onItemChange}
                          placeholder="Select date"
                          disabled={isReceiptDetailsLocked}
                          error={receiptDetailErrors.inboundDate}
                          preferredPlacement="bottom"
                        />
                      </DateFieldGroup>
                      <FieldGroup label="Time" error={receiptDetailErrors.inboundTime}>
                        <TextInput
                          name="inboundTime"
                          value={itemValues.inboundTime}
                          onChange={onItemChange}
                          placeholder="HH:MM AM/PM"
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.inboundTime)}
                        />
                      </FieldGroup>
                      <FieldGroup label="Plate No." error={receiptDetailErrors.inboundPlateNumber}>
                        <TextInput
                          name="inboundPlateNumber"
                          value={itemValues.inboundPlateNumber}
                          onChange={onItemChange}
                          placeholder="Enter plate no."
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.inboundPlateNumber)}
                        />
                      </FieldGroup>
                      <FieldGroup label="In Weight KG" error={receiptDetailErrors.inboundWeightKg}>
                        <TextInput
                          name="inboundWeightKg"
                          value={itemValues.inboundWeightKg}
                          onChange={onItemChange}
                          onBlur={onInboundWeightBlur}
                          type="text"
                          inputMode="decimal"
                          placeholder="Enter loaded truck kg"
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.inboundWeightKg)}
                        />
                      </FieldGroup>
                    </div>
                  </div>

                  <div className="grid gap-3 rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-3">
                    <h4 className="m-0 text-[0.78rem] font-semibold uppercase tracking-[0.1em] text-[var(--theme-text-muted)]">
                      Outbound Weighing
                    </h4>
                    <div className="grid gap-x-3 gap-y-1.5 sm:grid-cols-2 xl:grid-cols-3">
                      <DateFieldGroup>
                        <CustomDatePicker
                          label="Date"
                          name="outboundDate"
                          value={itemValues.outboundDate}
                          onChange={onItemChange}
                          placeholder="Select date"
                          disabled={isReceiptDetailsLocked}
                          error={receiptDetailErrors.outboundDate}
                          preferredPlacement="bottom"
                        />
                      </DateFieldGroup>
                      <FieldGroup label="Time" error={receiptDetailErrors.outboundTime}>
                        <TextInput
                          name="outboundTime"
                          value={itemValues.outboundTime}
                          onChange={onItemChange}
                          placeholder="HH:MM AM/PM"
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.outboundTime)}
                        />
                      </FieldGroup>
                      <FieldGroup label="Plate No." error={receiptDetailErrors.outboundPlateNumber}>
                        <TextInput
                          name="outboundPlateNumber"
                          value={itemValues.outboundPlateNumber}
                          onChange={onItemChange}
                          placeholder="Enter plate no."
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.outboundPlateNumber)}
                        />
                      </FieldGroup>
                      <FieldGroup label="Gross KG" error={receiptDetailErrors.grossKg}>
                        <TextInput
                          name="grossKg"
                          value={itemValues.grossKg}
                          onChange={onItemChange}
                          type="text"
                          inputMode="decimal"
                          placeholder="Enter gross kg"
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.grossKg)}
                        />
                      </FieldGroup>
                      <FieldGroup label="Tare KG" error={receiptDetailErrors.tareKg}>
                        <TextInput
                          name="tareKg"
                          value={itemValues.tareKg}
                          onChange={onItemChange}
                          onBlur={onWeightComputationBlur}
                          type="text"
                          inputMode="decimal"
                          placeholder="Enter tare kg"
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.tareKg)}
                        />
                      </FieldGroup>
                      <FieldGroup label="Net KG" error={receiptDetailErrors.netKg}>
                        <TextInput
                          name="netKg"
                          value={itemValues.netKg}
                          onChange={onItemChange}
                          type="text"
                          inputMode="decimal"
                          placeholder="Enter net kg"
                          disabled={isReceiptDetailsLocked}
                          hasError={Boolean(receiptDetailErrors.netKg)}
                        />
                      </FieldGroup>
                    </div>
                  </div>
                </div>
                {isAutofilling ? (
                  <div className="absolute inset-0 z-10 grid place-items-center rounded-[18px] border border-[var(--theme-border-soft)] bg-[color-mix(in_srgb,var(--theme-accent-soft)_50%,white)] backdrop-blur-[2px]">
                    <div className="rounded-full border border-[var(--theme-border)] bg-white px-5 py-2.5 text-[0.86rem] font-semibold text-[var(--theme-text-subtle)] shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.12)]">
                      Processing...
                    </div>
                  </div>
                ) : null}
              </div>
            </StepCard>

            <StepCard
              eyebrow="Step 2"
              stepNumber="2"
              title="Seaweed Details"
              description="Split the received stock into dry and wet quantities when needed."
              status={hasAutofilled ? 'current' : 'pending'}
            >
              <div className="grid gap-2.5">
                <div className="grid gap-2.5 sm:grid-cols-2">
                  <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-2.5 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.04)]">
                    <FieldGroup label={`Dry Quantity (${receivingUnit})`}>
                      <TextInput
                        name="dryQuantityKg"
                        value={itemValues.dryQuantityKg}
                        onChange={onItemChange}
                        onBlur={onDefaultZeroBlur}
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]+(?:\.[0-9]+)?"
                        placeholder="0"
                        disabled={isFormLocked}
                        hasError={
                          (shouldShowValidationErrors && Boolean(quantityRequiredError)) ||
                          (shouldShowValidationErrors && isQuantityValidationError(submitError))
                        }
                      />
                    </FieldGroup>
                  </div>
                  <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-2.5 shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.04)]">
                    <FieldGroup label={`Wet Quantity (${receivingUnit})`}>
                      <TextInput
                        name="wetQuantityKg"
                        value={itemValues.wetQuantityKg}
                        onChange={onItemChange}
                        onBlur={onDefaultZeroBlur}
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]+(?:\.[0-9]+)?"
                        placeholder="0"
                        disabled={isFormLocked}
                        hasError={
                          (shouldShowValidationErrors && Boolean(quantityRequiredError)) ||
                          (shouldShowValidationErrors && isQuantityValidationError(submitError))
                        }
                      />
                    </FieldGroup>
                  </div>
                </div>

                <div className="grid gap-2.5 sm:grid-cols-2">
                  <div
                    className={`rounded-[14px] border px-3 py-2.5 transition ${
                      isMoistureContentDisabled
                        ? 'border-[#d8dfe0] bg-[#eef1f1] shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]'
                        : 'border-[var(--theme-border-soft)] bg-white'
                    }`}
                  >
                    <FieldGroup
                      label="Moisture Content (%)"
                      error={shouldShowValidationErrors && !isMoistureContentDisabled ? moistureContentError : ''}
                    >
                      <TextInput
                        name="moistureContent"
                        value={itemValues.moistureContent}
                        onChange={onItemChange}
                        onBlur={onDefaultZeroBlur}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]+"
                        placeholder="0"
                        disabled={isMoistureContentDisabled}
                        hasError={
                          !isMoistureContentDisabled &&
                          ((shouldShowValidationErrors && Boolean(moistureContentError)) ||
                            (shouldShowValidationErrors && isMoistureValidationError(submitError)))
                        }
                      />
                    </FieldGroup>
                  </div>
                  <div className="rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-2.5">
                    <FieldGroup label="Reject (net KG)">
                      <TextInput
                        name="rejectQuantityKg"
                        value={itemValues.rejectQuantityKg}
                        onChange={onItemChange}
                        onBlur={onDefaultZeroBlur}
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]+(?:\.[0-9]+)?"
                        placeholder="0"
                        disabled={isFormLocked}
                        hasError={
                          (shouldShowValidationErrors && Boolean(quantityRequiredError)) ||
                          (shouldShowValidationErrors && isQuantityValidationError(submitError))
                        }
                      />
                    </FieldGroup>
                  </div>
                </div>

                <div className="grid gap-2.5">
                  <div className="rounded-[14px] border border-[var(--theme-border)] bg-[var(--theme-accent-soft)] px-4 py-2.5">
                    <span className="block text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-[var(--theme-text-subtle)]">
                      Total
                    </span>
                    <strong className="mt-0.5 block text-[1.12rem] font-semibold tracking-[-0.02em] text-[var(--theme-text)]">
                      {formatNumber(
                        receivedOrderQuantity
                      )}
                    </strong>
                    <span className="text-[0.78rem] font-medium text-[var(--theme-text-subtle)]">
                      {receivingUnit} received
                    </span>
                  </div>
                </div>
                {quantityMismatchWarning ? (
                  <InlineWarning message={quantityMismatchWarning} />
                ) : null}
                {quantityOverageWarning ? (
                  <InlineWarning message={quantityOverageWarning} />
                ) : null}
                {sacksOverageWarning ? (
                  <InlineWarning message={sacksOverageWarning} />
                ) : null}
                {netWeightOverageWarning ? (
                  <InlineWarning message={netWeightOverageWarning} />
                ) : null}

                <label className="grid gap-1.5 rounded-[14px] border border-[var(--theme-border-soft)] bg-white px-3 py-2.5">
                  <span className="text-[0.86rem] font-semibold text-[var(--theme-text-subtle)]">Remarks</span>
                  <textarea
                    className="min-h-[84px] resize-none rounded-[12px] border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.88rem] leading-5 outline-none transition placeholder:text-[var(--theme-text-muted)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]"
                    name="remarks"
                    value={itemValues.remarks}
                    onChange={onItemChange}
                    placeholder="Ex: too wet, with sand, good quality"
                    disabled={isFormLocked}
                  />
                </label>
              </div>

              {!isConfirmingSubmit && !isSaving && submitError ? (
                <FeedbackMessage tone="error" message={submitError} className="mt-3 text-[0.86rem]" />
              ) : null}
              {shouldShowValidationErrors && quantityRequiredError ? (
                <FeedbackMessage tone="error" message={quantityRequiredError} className="mt-3 text-[0.86rem]" />
              ) : null}
              {receiptImageError ? (
                <FeedbackMessage tone="error" message={receiptImageError} className="mt-3 text-[0.86rem]" />
              ) : null}

              {isReadOnlyMode ? (
                <div className="mt-3 flex justify-end">
                  <button
                    className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-6 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
                    type="button"
                    onClick={handleGuardedCancel}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <button
                    className="inline-flex min-h-[42px] items-center justify-center rounded-full bg-[var(--theme-accent)] px-6 text-[0.9rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.16)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] disabled:shadow-none"
                    type="button"
                    onClick={handleSubmitReceivingItem}
                    disabled={isSaving || !selectedPurchaseOrder}
                  >
                    {isDraftMode ? 'Save as Draft' : 'Submit'}
                  </button>
                  <button
                    className="inline-flex min-h-[42px] items-center justify-center rounded-full border border-[var(--theme-border)] bg-white px-6 text-[0.9rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    onClick={handleGuardedCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </StepCard>
          </div>
        </div>
      </div>
      <SaveConfirmationModal
        isOpen={isOcrCancelConfirmOpen}
        isConfirming={false}
        title="OCR is still processing"
        message="Receipt OCR is still processing. Closing now may lose the autofill result."
        confirmLabel="Close anyway"
        cancelLabel="Keep processing"
        onCancel={() => setIsOcrCancelConfirmOpen(false)}
        onConfirm={handleConfirmOcrCancel}
      />
    </ModalBackdrop>
  );
}

function StepCard({
  stepRef,
  eyebrow,
  stepNumber,
  title,
  description,
  status = 'pending',
  action = null,
  children,
}) {
  const isCurrent = status === 'current';
  const isDone = status === 'done';

  return (
    <section
      ref={stepRef}
      tabIndex={stepRef ? -1 : undefined}
      className={`rounded-[18px] border p-3 sm:p-4 ${isCurrent
        ? 'border-[var(--theme-accent)] bg-white shadow-[0_14px_28px_rgba(var(--theme-shadow-rgb),0.11)] ring-1 ring-[var(--theme-accent-soft)]'
        : isDone
          ? 'border-[var(--theme-border-soft)] bg-white shadow-[0_8px_18px_rgba(var(--theme-shadow-rgb),0.03)]'
          : 'border-[var(--theme-border-soft)] bg-white'
        }`}
    >
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[var(--theme-text-muted)]">
            {eyebrow}
          </span>
          <div className="mt-0.5 flex items-center gap-2.5">
            <div
              className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-[0.8rem] font-semibold ${isCurrent
                ? 'bg-[var(--theme-accent)] text-white'
                : isDone
                  ? 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]'
                  : 'bg-[var(--theme-surface-soft)] text-[var(--theme-text-strong)]'
                }`}
            >
              {stepNumber}
            </div>
            <div className="flex min-w-0 items-center gap-2">
              {isCurrent || isDone ? (
                <CheckCircle2
                  className={`h-4 w-4 shrink-0 ${isCurrent ? 'text-[var(--theme-accent)]' : 'text-[var(--theme-text-subtle)]'
                    }`}
                  aria-hidden="true"
                />
              ) : (
                <Circle className="h-4 w-4 shrink-0 text-[var(--theme-text-muted)]" aria-hidden="true" />
              )}
              <h4 className="m-0 truncate text-[0.96rem] font-semibold tracking-[-0.02em] text-[var(--theme-text)]">
                {title}
              </h4>
            </div>
          </div>
          <p className="m-0 mt-1 text-[0.8rem] leading-4 text-[var(--theme-text-muted)]">{description}</p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <span
            className={`rounded-full px-2.5 py-0.5 text-[0.66rem] font-semibold uppercase tracking-[0.08em] ${isCurrent
              ? 'bg-[var(--theme-accent)] text-white shadow-[0_6px_14px_rgba(var(--theme-shadow-rgb),0.2)]'
              : isDone
                ? 'bg-[var(--theme-accent-soft)] text-[var(--theme-accent)]'
                : 'bg-[var(--theme-surface-soft)] text-[var(--theme-text-muted)]'
              }`}
          >
            {isCurrent ? 'Current' : isDone ? 'Done' : 'Pending'}
          </span>
          {action}
        </div>
      </div>

      <div
        className={`mb-2 flex items-center gap-2 rounded-[12px] px-3 py-1.5 ${isCurrent ? 'bg-[var(--theme-accent-soft)]' : isDone ? 'bg-[var(--theme-surface-soft)]' : 'bg-[var(--theme-surface-soft)]'
          }`}
      >
        <span
          className={`h-2.5 w-2.5 rounded-full ${isCurrent
            ? 'bg-[var(--theme-accent)] shadow-[0_0_0_4px_rgba(var(--theme-shadow-rgb),0.16)]'
            : isDone
              ? 'bg-[var(--theme-text-muted)]'
              : 'bg-[var(--theme-border)]'
            }`}
          aria-hidden="true"
        />
        <span
          className={`text-[0.72rem] font-semibold uppercase tracking-[0.08em] ${isCurrent ? 'text-[var(--theme-text-subtle)]' : 'text-[var(--theme-text-muted)]'
            }`}
        >
          {isCurrent ? 'Current step' : isDone ? 'Reviewed' : 'Pending'}
        </span>
      </div>
      <div className="border-t border-[var(--theme-border-soft)] pt-2.5">{children}</div>
    </section>
  );
}

function ConfirmReceivingModal({ isDraft, isSaving, onCancel, onConfirm }) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(33,27,22,0.16)]"
      contentClassName="items-center"
    >
      <div className="w-full max-w-md rounded-[24px] border border-[var(--theme-border-soft)] bg-white p-6 shadow-[0_22px_60px_rgba(var(--theme-shadow-rgb),0.14)]">
        <h3 className="m-0 text-[1.15rem] font-semibold tracking-[-0.03em] text-[var(--theme-text)]">
          {isDraft ? 'Confirm draft save?' : 'Do you confirm?'}
        </h3>
        <p className="m-0 mt-3 text-[0.96rem] leading-6 text-[var(--theme-text-muted)]">
          {isDraft
            ? 'This will save the receiving details as draft while the purchase order remains Initial.'
            : 'This will submit the receiving record, mark the purchase order as received, and update inventory.'}
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <button
            className="rounded-full border border-[var(--theme-border)] bg-white px-5 py-3 text-[0.94rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
            type="button"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="rounded-full bg-[var(--theme-accent)] px-5 py-3 text-[0.94rem] font-semibold text-white shadow-[0_10px_22px_rgba(var(--theme-shadow-rgb),0.16)] transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)] disabled:shadow-none"
            type="button"
            onClick={onConfirm}
            disabled={isSaving}
          >
            {isSaving ? (isDraft ? 'Saving...' : 'Submitting...') : isDraft ? 'Save as Draft' : 'Confirm'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  );
}

function ReceivingFilters({ values, suggestions, onChange, onSubmit, onReset }) {
  return (
    <FilterPanel
      onSubmit={onSubmit}
      onReset={onReset}
      layoutClassName="grid gap-2.5 md:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_170px_190px_auto]"
    >
      <FilterField
        label="Search"
        name="supplierName"
        type="search"
        value={values.supplierName}
        onChange={onChange}
        placeholder="Search P.O. code, supplier, sacks mark, or lansa"
        suggestions={suggestions}
      />
      <FilterField
        label="Arrival Date"
        name="date"
        type="date"
        value={values.date}
        onChange={onChange}
      />
      <FilterField
        label="Status"
        name="status"
        value={values.status}
        onChange={onChange}
        options={[
          { value: 'Initial + In Progress', label: 'Initial + In Progress' },
          { value: 'All', label: 'All' },
          { value: 'Initial', label: 'Initial' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Received', label: 'Received' },
          { value: 'Completed', label: 'Completed' },
        ]}
      />
    </FilterPanel>
  );
}

function ReceiptPreview({ imageUrl, isLoading, onReadReceipt, onRetake, isReadOnly = false }) {
  const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);

  return (
    <>
      <div className="mb-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-app-bg)] p-2.5">
        <div className="grid gap-3 sm:grid-cols-[82px_1fr]">
          <div className="group relative h-20 w-full overflow-hidden rounded-[10px] border border-[var(--theme-border-soft)] bg-white sm:w-[82px]">
            <img
              className="h-full w-full object-cover"
              src={imageUrl}
              alt="Receipt preview"
            />
            <button
              className="absolute inset-0 grid place-items-center bg-[rgba(31,24,18,0.08)] text-white opacity-100 transition hover:bg-[rgba(31,24,18,0.18)] sm:opacity-0 sm:group-hover:opacity-100"
              type="button"
              onClick={() => setIsImagePreviewOpen(true)}
              disabled={isLoading}
              aria-label="View selected receipt image"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full bg-[rgba(31,24,18,0.68)] shadow-[0_10px_24px_rgba(31,24,18,0.22)]">
                <Eye className="h-4 w-4" aria-hidden="true" />
              </span>
            </button>
          </div>
          <div className="flex flex-col justify-between gap-3">
            <div>
              <strong className="block text-[0.9rem] text-[var(--theme-text)]">Review receipt photo</strong>
              <p className="m-0 mt-1 text-[0.78rem] leading-5 text-[var(--theme-text-muted)]">
                Make sure the full receipt is clear, flat, and readable before reading it.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
                type="button"
                onClick={() => setIsImagePreviewOpen(true)}
                disabled={isLoading}
              >
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                View
              </button>
              {isReadOnly ? null : (
                <>
                  <button
                    className="rounded-full bg-[var(--theme-accent)] px-4 py-2 text-[0.82rem] font-semibold text-white transition hover:bg-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:border disabled:border-[var(--theme-border)] disabled:bg-[var(--theme-accent-soft)] disabled:text-[var(--theme-accent-strong)]"
                    type="button"
                    onClick={onReadReceipt}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Reading...' : 'Automate'}
                  </button>
                  <button
                    className="rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-[0.82rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] disabled:cursor-not-allowed disabled:opacity-60"
                    type="button"
                    onClick={onRetake}
                    disabled={isLoading}
                  >
                    Retake
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {isImagePreviewOpen ? (
        <ReceiptImagePreviewModal
          imageUrl={imageUrl}
          onClose={() => setIsImagePreviewOpen(false)}
        />
      ) : null}
    </>
  );
}

function ReceiptImagePreviewModal({ imageUrl, onClose }) {
  return (
    <ModalBackdrop
      backdropClassName="bg-[rgba(var(--theme-shadow-rgb),0.52)]"
      contentClassName="items-center"
      zIndexClassName="z-[70]"
    >
      <div className="w-full max-w-4xl overflow-hidden rounded-[20px] border border-[var(--theme-border-soft)] bg-white shadow-[0_26px_70px_rgba(var(--theme-shadow-rgb),0.28)]">
        <div className="flex items-center justify-between border-b border-[var(--theme-border-soft)] px-4 py-3">
          <strong className="text-[0.98rem] font-semibold text-[var(--theme-text)]">Receipt Image</strong>
          <button
            className="grid h-9 w-9 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            type="button"
            onClick={onClose}
            aria-label="Close receipt image preview"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        <div className="max-h-[78vh] overflow-auto bg-white p-3">
          <img
            className="mx-auto max-h-none w-auto max-w-full rounded-[12px] border border-[var(--theme-border-soft)] bg-white object-contain"
            src={imageUrl}
            alt="Selected receipt full preview"
          />
        </div>
      </div>
    </ModalBackdrop>
  );
}

function ReceivingItemsTable({ purchaseOrders, onOpenReceivingModal }) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[1280px] table-fixed border-collapse">
          <colgroup>
            <col className="w-[88px]" />
            <col className="w-[150px]" />
            <col className="w-[135px]" />
            <col className="w-[115px]" />
            <col className="w-[112px]" />
            <col className="w-[112px]" />
            <col className="w-[125px]" />
            <col className="w-[112px]" />
            <col className="w-[78px]" />
          </colgroup>
          <thead>
            <tr className="bg-[var(--theme-accent-panel)]">
              <TableHeading>P.O. Code</TableHeading>
              <TableHeading>
                <span className="inline-flex items-baseline gap-1">
                  <span>Supplier</span>
                  <span className="text-[0.56rem] font-medium normal-case tracking-normal text-[var(--theme-accent-panel-text)] opacity-75">
                    (Sacks Mark+Company)
                  </span>
                </span>
              </TableHeading>
              <TableHeading>Requested Sacks / KG</TableHeading>
              <TableHeading>Lansa Name</TableHeading>
              <TableHeading>Arrival Date</TableHeading>
              <TableHeading>Received Date</TableHeading>
              <TableHeading>Total Received</TableHeading>
              <TableHeading>Status</TableHeading>
              <TableHeading>Action</TableHeading>
            </tr>
          </thead>
          <tbody>
            {purchaseOrders.length ? (
              purchaseOrders.map((purchaseOrder) => {
                const purchaseOrderCode = formatPurchaseOrderCode(purchaseOrder);
                const isArrivalToday = isReceivingArrivalToday(purchaseOrder.arrivalDate);

                return (
                  <tr
                    key={purchaseOrder.id}
                    className={`border-t border-[var(--theme-border-soft)] transition-colors ${
                      isArrivalToday
                        ? 'bg-[#fff6e2] hover:bg-[#ffefc8]'
                        : 'hover:bg-[var(--theme-surface-soft)]'
                    }`}
                  >
                    <TableCell>{purchaseOrderCode}</TableCell>
                    <TableCell>
                      <span
                        className="block max-w-[180px] truncate"
                        title={formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                      >
                        {formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {formatQuantity(
                        purchaseOrder.expectedQuantity,
                        purchaseOrder.stockUnit
                      )}
                    </TableCell>
                    <TableCell>{purchaseOrder.lansaName || '-'}</TableCell>
                    <TableCell>
                      <ArrivalDateValue value={purchaseOrder.arrivalDate} />
                    </TableCell>
                    <TableCell>{formatTableDate(purchaseOrder.receivedDate)}</TableCell>
                    <TableCell>
                      {formatReceivedQuantity(
                        purchaseOrder.totalReceivedQuantity,
                        purchaseOrder.stockUnit
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1.5">
                        <PurchaseOrderStatusBadge status={purchaseOrder.status} />
                        <ReceivingDraftBadge purchaseOrder={purchaseOrder} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <ReceivingActionButton
                        purchaseOrder={purchaseOrder}
                        purchaseOrderCode={purchaseOrderCode}
                        onOpenReceivingModal={onOpenReceivingModal}
                      />
                    </TableCell>
                  </tr>
                );
              })
            ) : (
              <tr className="border-t border-[var(--theme-border-soft)]">
                <td className="px-6 py-14" colSpan={9}>
                  <EmptyState />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 p-4 md:hidden">
        {purchaseOrders.length ? (
          purchaseOrders.map((purchaseOrder) => {
            const purchaseOrderCode = formatPurchaseOrderCode(purchaseOrder);
            const isArrivalToday = isReceivingArrivalToday(purchaseOrder.arrivalDate);

            return (
              <article
                key={purchaseOrder.id}
                className={`rounded-[18px] border p-3 transition duration-150 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.11)] ${
                  isArrivalToday
                    ? 'border-[#e9c06e] bg-[#fff6e2] hover:bg-[#ffefc8]'
                    : 'border-[var(--theme-border)] bg-[var(--theme-surface-muted)] hover:bg-[var(--theme-surface-soft)]'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <strong className="block text-[1rem] text-[var(--theme-text)]">
                      {purchaseOrderCode}
                    </strong>
                    <span className="mt-1 block text-[0.92rem] text-[var(--theme-text-muted)]">
                      {formatSupplierDisplay(purchaseOrder.sacksMark, purchaseOrder.supplierName)}
                    </span>
                  </div>
                  <ReceivingActionButton
                    purchaseOrder={purchaseOrder}
                    purchaseOrderCode={purchaseOrderCode}
                    onOpenReceivingModal={onOpenReceivingModal}
                  />
                </div>

                <div className="mt-3 grid gap-1.5 text-[0.88rem] text-[var(--theme-text-subtle)]">
                  <RecordMeta
                    label="Requested Sacks / KG"
                    value={formatQuantity(
                      purchaseOrder.expectedQuantity,
                      purchaseOrder.stockUnit
                    )}
                  />
                  <RecordMeta label="Lansa Name" value={purchaseOrder.lansaName || '-'} />
                  <RecordMeta label="Arrival Date" value={<ArrivalDateValue value={purchaseOrder.arrivalDate} />} />
                  <RecordMeta label="Received Date" value={formatTableDate(purchaseOrder.receivedDate)} />
                  <RecordMeta
                    label="Total Received"
                    value={formatReceivedQuantity(
                      purchaseOrder.totalReceivedQuantity,
                      purchaseOrder.stockUnit
                    )}
                  />
                  <RecordMeta
                    label="Status"
                    value={
                      <span className="flex flex-wrap justify-end gap-1.5">
                        <PurchaseOrderStatusBadge status={purchaseOrder.status} />
                        <ReceivingDraftBadge purchaseOrder={purchaseOrder} />
                      </span>
                    }
                  />
                </div>
              </article>
            );
          })
        ) : (
          <div className="rounded-[18px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-muted)] px-5 py-12">
            <EmptyState />
          </div>
        )}
      </div>
    </div>
  );
}

function ReceivingActionButton({ purchaseOrder, purchaseOrderCode, onOpenReceivingModal }) {
  const isEditable = purchaseOrder.status === 'In Progress';
  const isDraftEditable = purchaseOrder.status === 'Initial';
  const isReadOnly = ['Received', 'Completed'].includes(purchaseOrder.status);
  const canOpenReceiving = isEditable || isDraftEditable || isReadOnly;

  return (
    <ActionIconButton
      label={
        isEditable
          ? `Edit receiving for ${purchaseOrderCode}`
          : isDraftEditable
            ? `Edit draft receiving for ${purchaseOrderCode}`
            : isReadOnly
              ? `View receiving for ${purchaseOrderCode}`
              : `Receiving completed for ${purchaseOrderCode}`
      }
      onClick={() => onOpenReceivingModal(purchaseOrder)}
      tone={isReadOnly ? 'neutral' : canOpenReceiving ? 'neutral' : 'success'}
      disabled={!canOpenReceiving}
    >
      {isEditable ? (
        <PencilLine className="h-4 w-4" aria-hidden="true" />
      ) : isDraftEditable ? (
        <ClipboardList className="h-4 w-4" aria-hidden="true" />
      ) : isReadOnly ? (
        <Eye className="h-4 w-4" aria-hidden="true" />
      ) : (
        <CheckCircle2 className="h-4 w-4 text-[#4f8f42]" aria-hidden="true" />
      )}
    </ActionIconButton>
  );
}

function ReceivingDraftBadge({ purchaseOrder }) {
  if (!hasStaffReceivingDraft(purchaseOrder)) {
    return null;
  }

  return (
    <span className="inline-flex rounded-full border border-[#a8d3ad] bg-[#edf8ef] px-2.5 py-0.5 text-[0.7rem] font-semibold text-[#2f7a3f]">
      Draft saved
    </span>
  );
}

function hasStaffReceivingDraft(purchaseOrder) {
  return (
    purchaseOrder?.status === 'Initial' &&
    Array.isArray(purchaseOrder.receivingItems) &&
    purchaseOrder.receivingItems.length > 0
  );
}

function getReceivingDraftReceiptImageUrl(purchaseOrder) {
  if (!Array.isArray(purchaseOrder?.receivingItems) || !purchaseOrder.receivingItems.length) {
    return '';
  }

  const imagePath = (purchaseOrder.receivingItems || [])
    .map((item) => item.image)
    .find(Boolean);

  return imagePath ? buildAssetUrl(imagePath) : '';
}

function ReceivingPageSkeleton() {
  return (
    <div className="grid gap-3" aria-label="Loading receiving records">
      <div className="rounded-[22px] border border-[var(--theme-border-soft)] bg-white p-3 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.06)]">
        <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-[minmax(0,1.2fr)_170px_190px_auto]">
          <SkeletonField className="h-16" />
          <SkeletonField className="h-16" />
          <SkeletonField className="h-16" />
          <div className="grid grid-cols-2 gap-2 self-end">
            <SkeletonBlock className="h-10 rounded-full" />
            <SkeletonBlock className="h-10 rounded-full" />
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-[22px] border border-[var(--theme-border)] bg-white shadow-[0_14px_30px_rgba(var(--theme-shadow-rgb),0.08)]">
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[1180px] table-fixed border-collapse">
            <colgroup>
              <col className="w-[78px]" />
              <col className="w-[88px]" />
              <col className="w-[150px]" />
              <col className="w-[135px]" />
              <col className="w-[115px]" />
              <col className="w-[112px]" />
              <col className="w-[112px]" />
              <col className="w-[125px]" />
            </colgroup>
            <thead>
              <tr className="bg-[var(--theme-accent-panel)]">
                {Array.from({ length: 8 }).map((_, index) => (
                  <th key={index} className="h-[50px] px-3 py-2.5 text-left align-middle lg:px-4">
                    <SkeletonBlock
                      className={`h-3 rounded-full ${
                        index === 0 ? 'w-10' : index === 2 ? 'w-24' : 'w-16'
                      }`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 7 }).map((_, rowIndex) => (
                <tr key={rowIndex} className="border-t border-[var(--theme-border-soft)]">
                  {Array.from({ length: 8 }).map((__, columnIndex) => (
                    <td key={columnIndex} className="px-3 py-2 align-middle lg:px-4">
                      {columnIndex === 0 ? (
                        <SkeletonBlock className="h-9 w-9 rounded-full" />
                      ) : (
                        <SkeletonBlock
                          className={`h-3 rounded-full ${
                            columnIndex === 2
                              ? 'w-28'
                              : columnIndex === 3 || columnIndex === 7
                                ? 'w-20'
                                : 'w-16'
                          }`}
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid gap-3 p-4 md:hidden">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="rounded-[18px] border border-[var(--theme-border)] bg-[var(--theme-surface-muted)] p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="grid flex-1 gap-2">
                  <SkeletonBlock className="h-4 w-24 rounded-full" />
                  <SkeletonBlock className="h-3 w-36 rounded-full" />
                </div>
                <SkeletonBlock className="h-9 w-9 rounded-full" />
              </div>
              <div className="mt-4 grid gap-2">
                {Array.from({ length: 5 }).map((__, metaIndex) => (
                  <div key={metaIndex} className="flex items-center justify-between gap-4">
                    <SkeletonBlock className="h-3 w-24 rounded-full" />
                    <SkeletonBlock className="h-3 w-20 rounded-full" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
        <div className="flex flex-col items-center gap-1.5 border-t border-[var(--theme-border-soft)] bg-white px-4 py-3 sm:flex-row sm:justify-between sm:gap-3">
          <SkeletonBlock className="h-4 w-36 rounded-full" />
          <div className="flex items-center gap-3">
            <SkeletonBlock className="h-9 w-16 rounded-xl" />
            <SkeletonBlock className="h-4 w-20 rounded-full" />
            <SkeletonBlock className="h-9 w-16 rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SkeletonField({ className = '' }) {
  return (
    <div className={`grid gap-2 ${className}`}>
      <SkeletonBlock className="h-3 w-20 rounded-full" />
      <SkeletonBlock className="h-10 rounded-full" />
    </div>
  );
}

function SkeletonBlock({ className = '' }) {
  return (
    <span
      className={`block animate-pulse bg-[linear-gradient(90deg,var(--theme-border-soft)_0%,var(--theme-surface-soft)_45%,var(--theme-border-soft)_100%)] bg-[length:220%_100%] ${className}`}
      aria-hidden="true"
    />
  );
}

function FieldGroup({ label, error = '', children }) {
  return (
    <label className="grid min-h-[70px] grid-rows-[18px_34px_14px] gap-0.5">
      <span className="self-end text-[0.8rem] font-medium leading-[18px] text-[var(--theme-text-subtle)]">{label}</span>
      {children}
      <span
        className={`min-h-[14px] truncate text-[0.64rem] leading-[14px] text-[#bb5745] ${
          error ? 'visible' : 'invisible'
        }`}
        title={error || undefined}
      >
        {error || 'No error'}
      </span>
    </label>
  );
}

function DateFieldGroup({ children }) {
  return (
    <div className="grid min-h-[70px] gap-0.5 [&>label]:gap-0.5 [&>label>button]:min-h-[34px] [&>label>button]:rounded-[12px] [&>label>button]:py-1.5 [&>label>span:first-child]:self-end [&>label>span:first-child]:text-[0.8rem] [&>label>span:first-child]:font-medium [&>label>span:first-child]:leading-[18px] [&>label>span:first-child]:text-[var(--theme-text-subtle)]">
      {children}
    </div>
  );
}

function TextInput({ hasError = false, className = '', ...props }) {
  return (
    <input
      className={`min-h-[34px] w-full rounded-[12px] border bg-white px-3 text-[0.84rem] outline-none transition placeholder:text-[var(--theme-text-muted)] ${hasError
        ? 'border-[#d06d5c] text-[#8f3d2f] focus:border-[#c76552]'
        : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)]'
        } disabled:cursor-not-allowed disabled:border-[#d8dfe0] disabled:bg-[#eef1f1] disabled:text-[var(--theme-text-muted)] disabled:placeholder:text-[var(--theme-text-muted)] disabled:shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] disabled:focus:border-[#d8dfe0] ${className}`}
      {...props}
    />
  );
}

function InlineWarning({ message }) {
  return (
    <div className="mt-1.5 flex items-start gap-1.5 rounded-[10px] border border-[#ead7a8] bg-[#fff8e6] px-2.5 py-2 text-[0.72rem] font-medium leading-4 text-[#8a6424]">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

function CompactInfoTile({ label, value }) {
  return (
    <div className="min-w-0">
      <span className="block text-[0.63rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-muted)]">
        {label}
      </span>
      <strong className="mt-0.5 block truncate text-[0.86rem] font-semibold text-[var(--theme-text)]">
        {value || '-'}
      </strong>
    </div>
  );
}

function TableHeading({ children }) {
  return (
    <th className="h-[50px] whitespace-nowrap px-3 py-2.5 text-left align-middle text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[var(--theme-accent-panel-text)] lg:px-4">
      {children}
    </th>
  );
}

function TableCell({ children }) {
  return (
    <td className="truncate whitespace-nowrap px-3 py-2 align-middle text-[0.84rem] text-[var(--theme-text)] lg:px-4">
      {children}
    </td>
  );
}

function RecordMeta({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[var(--theme-text-muted)]">{label}</span>
      <div className="text-right font-medium text-[var(--theme-text)]">{value || '-'}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center text-center text-[var(--theme-text-muted)]">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--theme-accent-soft)] text-[var(--theme-text-muted)]">
        <ClipboardList className="h-6 w-6" aria-hidden="true" />
      </div>
      <strong className="mt-4 text-[1rem] font-medium text-[var(--theme-text-strong)]">
        No in-progress purchase orders found
      </strong>
    </div>
  );
}

function formatQuantity(value, unit) {
  return `${formatNumber(value)} ${unit === 'sacks' ? 'sacks' : 'kg'}`;
}

function formatPurchaseOrderCode(purchaseOrder) {
  return purchaseOrder.poCode || `PO-${String(purchaseOrder.id).padStart(4, '0')}`;
}

function formatSupplierDisplay(sacksMark, supplierName) {
  const normalizedSacksMark = String(sacksMark || '').trim();
  const normalizedSupplierName = String(supplierName || '').trim();

  if (normalizedSacksMark && normalizedSupplierName) {
    return `${normalizedSacksMark} - ${normalizedSupplierName}`;
  }

  return normalizedSacksMark || normalizedSupplierName || '-';
}

function formatReceivedQuantity(value, unit = 'kg') {
  const quantity = Number(value || 0);

  if (quantity <= 0) {
    return '';
  }

  return `${formatNumber(quantity)} ${unit === 'kg' ? 'kg' : 'sacks'}`;
}

function formatTableDate(value) {
  if (!value) {
    return '';
  }

  return formatShortDate(value);
}

function ArrivalDateValue({ value }) {
  const isToday = isReceivingArrivalToday(value);

  return (
    <span className="inline-flex items-center gap-2">
      <span>{formatTableDate(value)}</span>
      {isToday ? (
        <span className="rounded-full border border-[#f0c46d] bg-[#fff0c7] px-1.5 py-0.5 text-[0.64rem] font-semibold uppercase tracking-[0.06em] text-[#8a5a00]">
          Today
        </span>
      ) : null}
    </span>
  );
}

function isReceivingArrivalToday(value) {
  if (!value) {
    return false;
  }

  return String(value).slice(0, 10) === getTodayInManila();
}

function getTodayInManila() {
  const dateParts = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'Asia/Manila',
    year: 'numeric',
  }).formatToParts(new Date());
  const mappedParts = dateParts.reduce((parts, part) => {
    parts[part.type] = part.value;
    return parts;
  }, {});

  return `${mappedParts.year}-${mappedParts.month}-${mappedParts.day}`;
}

function isMoistureValidationError(message) {
  return /moisture/i.test(message || '');
}

function isQuantityValidationError(message) {
  return /quantity|dry|wet/i.test(message || '');
}

function isValidStepTwoNumberValue(value) {
  return /^\d+(?:\.\d+)?$/.test(String(value || '').trim());
}

function isValidWholeNumberValue(value) {
  return /^\d+$/.test(String(value || '').trim());
}

function getPositiveRequiredError(value, label) {
  const normalizedValue = String(value || '').trim();
  const numericValue = Number(normalizedValue);

  if (!normalizedValue || !Number.isFinite(numericValue) || numericValue <= 0) {
    return `${label} must be greater than 0.`;
  }

  return '';
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

export default StaffReceivingPage;
